import React, { useState } from 'react';
import { BarChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { BarChart2, TrendingUp, Pencil } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика инвестиций и роста капитала
 */
const InvestmentChart = ({ chartData, results, useInflationAdjusted }) => {
  const [activeChart, setActiveChart] = useState('capital');
  const [showInflationAdjusted, setShowInflationAdjusted] = useState(useInflationAdjusted);
  
  if (!chartData || chartData.length === 0 || !results) {
    return null;
  }

  // Обработка данных для отображения на графике
  const processData = (data) => {
    return data.map((item) => {
      const adjustedTotalSavings = showInflationAdjusted ? item.totalSavingsInflationAdjusted : item.totalSavings;
      const adjustedPassiveIncome = showInflationAdjusted ? item.projectedPassiveIncomeAtRetirementInflationAdjusted : item.projectedPassiveIncomeAtRetirement;
      
      return {
        age: item.year,
        year: new Date().getFullYear() + (item.year - parseInt(results.currentAge, 10)),
        capital: adjustedTotalSavings,
        passiveIncome: item.projectedPassiveIncome,
        passiveIncomeAdjusted: item.projectedPassiveIncomeInflationAdjusted,
      };
    });
  };

  const data = processData(chartData);
  
  // Формат для чисел на оси Y
  const yAxisTickFormatter = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)} млн`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)} тыс`;
    }
    return value;
  };

  // Формат для всплывающих подсказок
  const tooltipFormatter = (value, name) => {
    if (name === 'capital') {
      return [formatCurrency(value), 'Капитал'];
    } else if (name === 'passiveIncome') {
      return [formatCurrency(value), 'Пассивный доход в месяц'];
    } else if (name === 'passiveIncomeAdjusted') {
      return [formatCurrency(value), 'Пассивный доход с учетом инфляции'];
    }
    return [formatNumber(value), name];
  };

  // Настройка цветов графиков
  const colorMap = {
    capital: '#6366f1', // indigo-500
    passiveIncome: '#10b981', // emerald-500
    passiveIncomeAdjusted: '#f59e0b' // amber-500
  };

  // Ключевые точки для отображения на графике
  const targetRetirementAge = parseInt(results.retirementAge, 10);
  const financeIndependenceAge = results.financialIndependenceAge ? parseInt(results.financialIndependenceAge, 10) : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Прогноз накоплений и пассивного дохода
        </h3>

        {/* Переключатель типа графика */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setActiveChart('capital')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeChart === 'capital'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Капитал
          </button>
          <button
            onClick={() => setActiveChart('income')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeChart === 'income'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Пассивный доход
          </button>
          <button
            onClick={() => setActiveChart('combined')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeChart === 'combined'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Комбинированный
          </button>
        </div>
      </div>

      {/* Переключатель учета инфляции */}
      {useInflationAdjusted && (
        <div className="mb-4 flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showInflationAdjusted}
              onChange={() => setShowInflationAdjusted(!showInflationAdjusted)}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Показать значения с учетом инфляции
            </span>
          </label>
        </div>
      )}

      {/* График */}
      <div className="h-80 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          {activeChart === 'capital' ? (
            <LineChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="age" 
                label={{ 
                  value: 'Возраст', 
                  position: 'insideBottomRight', 
                  offset: -5 
                }} 
              />
              <YAxis 
                tickFormatter={yAxisTickFormatter} 
                label={{ 
                  value: 'Капитал (₽)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }} 
              />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {targetRetirementAge && (
                <ReferenceLine 
                  x={targetRetirementAge} 
                  stroke="#f43f5e" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Выход на пенсию', 
                    position: 'top', 
                    fill: '#f43f5e' 
                  }} 
                />
              )}
              {results.targetCapital && (
                <ReferenceLine 
                  y={results.targetCapital} 
                  stroke="#f59e0b" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Целевой капитал', 
                    position: 'right', 
                    fill: '#f59e0b' 
                  }} 
                />
              )}
              <Line 
                type="monotone" 
                dataKey="capital" 
                name="Капитал" 
                stroke={colorMap.capital} 
                strokeWidth={2} 
                dot={{ r: 1 }} 
                activeDot={{ r: 5 }} 
              />
            </LineChart>
          ) : activeChart === 'income' ? (
            <LineChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="age" 
                label={{ 
                  value: 'Возраст', 
                  position: 'insideBottomRight', 
                  offset: -5 
                }} 
              />
              <YAxis 
                tickFormatter={yAxisTickFormatter} 
                label={{ 
                  value: 'Доход в месяц (₽)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }} 
              />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {targetRetirementAge && (
                <ReferenceLine 
                  x={targetRetirementAge} 
                  stroke="#f43f5e" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Выход на пенсию', 
                    position: 'top', 
                    fill: '#f43f5e' 
                  }} 
                />
              )}
              {financeIndependenceAge && (
                <ReferenceLine 
                  x={financeIndependenceAge} 
                  stroke="#10b981" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Финансовая независимость', 
                    position: 'top', 
                    fill: '#10b981' 
                  }} 
                />
              )}
              <Line 
                type="monotone" 
                dataKey="passiveIncome" 
                name="Пассивный доход в месяц" 
                stroke={colorMap.passiveIncome} 
                strokeWidth={2} 
                dot={{ r: 1 }} 
                activeDot={{ r: 5 }} 
              />
              {showInflationAdjusted && (
                <Line 
                  type="monotone" 
                  dataKey="passiveIncomeAdjusted" 
                  name="Пассивный доход с учетом инфляции" 
                  stroke={colorMap.passiveIncomeAdjusted} 
                  strokeWidth={2} 
                  dot={{ r: 1 }} 
                  activeDot={{ r: 5 }} 
                  strokeDasharray="5 5"
                />
              )}
              {/* Линия желаемого пассивного дохода */}
              <ReferenceLine 
                y={results.desiredPassiveIncome} 
                stroke="#f59e0b" 
                strokeDasharray="3 3" 
                label={{ 
                  value: 'Желаемый доход', 
                  position: 'right', 
                  fill: '#f59e0b' 
                }} 
              />
            </LineChart>
          ) : (
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="age" 
                label={{ 
                  value: 'Возраст', 
                  position: 'insideBottomRight', 
                  offset: -5 
                }} 
              />
              <YAxis 
                yAxisId="left" 
                orientation="left" 
                tickFormatter={yAxisTickFormatter} 
                label={{ 
                  value: 'Капитал (₽)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }} 
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                tickFormatter={yAxisTickFormatter} 
                label={{ 
                  value: 'Пассивный доход (₽)', 
                  angle: 90, 
                  position: 'insideRight' 
                }} 
              />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {targetRetirementAge && (
                <ReferenceLine 
                  x={targetRetirementAge} 
                  stroke="#f43f5e" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Выход на пенсию', 
                    position: 'top', 
                    fill: '#f43f5e' 
                  }} 
                />
              )}
              <Bar 
                yAxisId="left" 
                dataKey="capital" 
                name="Капитал" 
                fill={colorMap.capital} 
                opacity={0.8} 
              />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="passiveIncome" 
                name="Пассивный доход в месяц" 
                stroke={colorMap.passiveIncome} 
                strokeWidth={2} 
                dot={{ r: 1 }} 
                activeDot={{ r: 5 }} 
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Пояснение к графику */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        <div className="flex items-start">
          <Pencil className="w-4 h-4 mt-0.5 mr-2 text-gray-500 dark:text-gray-400" />
          <p>
            {activeChart === 'capital' 
              ? 'График показывает прогнозируемый рост капитала по годам. Горизонтальная линия отображает целевой капитал для финансовой независимости, вертикальная - планируемый возраст выхода на пенсию.' 
              : activeChart === 'income'
                ? 'График показывает прогнозируемый пассивный доход по годам. Горизонтальная линия отображает желаемый уровень пассивного дохода.'
                : 'Комбинированный график показывает одновременно рост капитала (столбцы) и пассивного дохода (линия).'}
            {showInflationAdjusted && ' Значения скорректированы с учетом инфляции для отображения реальной покупательной способности.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvestmentChart;